import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SimpleVenueData, VenueData } from '../../../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
  VenueAdmissionType,
  VenueLifecycleStatus,
  VenueType,
} from '../../../shared/enum/EnumRepository';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useAuthTokenStore } from '../../../shared/store/authStore';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import {
  VENUE_BULK_FILE_MAX_SIZE,
  VENUE_BULK_FILE_MAX_SIZE_TEXT,
  VENUE_BULK_MAX_ROWS,
} from '../../../shared/constants/limits';
import { sizeCheckBig } from '../../../shared/util/fileSizeCheck';
import { useAdminVenue } from '../../share/hook/useAdminVenue';
import { useAdminVenueListMarquee } from '../../share/hook/useAdminVenueListMarquee';
import { useAdminVenueListSelection } from '../../share/hook/useAdminVenueListSelection';
import { useAdminVenueStore } from '../../share/store/adminVenueStore';
import EmptyList from '../../share/component/EmptyList';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import createIcon from '../../../asset/icon/create.png';
import downloadIcon from '../../../asset/icon/download.png';
import listIcon from '../../../asset/icon/list.png';
import VenueInfo from './VenueInfo';
import VenueViewer from './VenueViewer';
import VenueBulkPreview from './VenueBulkPreview';
import VenueBulkResult from './VenueBulkResult';
import MultiVenueViewer from './MultiVenueViewer';
import VenueViewerShell from './VenueViewerShell';
import { downloadVenueBulkTemplate } from './buildVenueBulkTemplate';
import { runAfterUnsavedVenueCheck } from './runAfterUnsavedVenueCheck';
import {
  parseVenueBulkSheet,
  VenueBulkSheetParseError,
  VenueBulkSheetParseResult,
} from './parseVenueBulkSheet';
import { getVenueTagList } from '../../../shared/api/venueTagApi';

const emptyVenue = (): VenueData => ({
  id: '',
  venueCode: '',
  name: '',
  nameEn: '',
  type: VenueType.GALLERY,
  lifecycleStatus: VenueLifecycleStatus.ACTIVE,
  countryCode: 'KR',
  city: '',
  district: '',
  roadAddress: '',
  roadAddressEn: '',
  latitude: null,
  longitude: null,
  admissionType: VenueAdmissionType.UNKNOWN,
  timeZone: 'Asia/Seoul',
  instagramUrl: '',
  websiteUrl: '',
  phoneNumber: '',
  description: '',
  descriptionEn: '',
  naverMapUrl: '',
  googleMapUrl: '',
  admissionNote: '',
  openingHoursNote: '',
  openingHours: [],
  tags: [],
});

const VenueList: React.FC = () => {
  const { user } = useAuth();
  const authToken = useAuthTokenStore((state) => state.authToken);
  const {
    venueList,
    activeVenue,
    multiSelectedVenueIds,
    bulkResult,
    setActiveVenue,
    setBulkResult,
    selectVenue,
    selectVenues,
    createBulk,
    load,
  } = useAdminVenue();
  const pageSize = useAdminVenueStore((state) => state.pageSize);
  const filterData = useAdminVenueStore((state) => state.filterData);
  const sortData = useAdminVenueStore((state) => state.sortData);
  const listRef = useRef<HTMLElement>(null);
  const venueListCompRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const [bulkPreview, setBulkPreviewState] = useState<{
    fileName: string;
    result: VenueBulkSheetParseResult;
  } | null>(null);
  const setBulkPreview = (val: { fileName: string; result: VenueBulkSheetParseResult } | null) => {
    setBulkPreviewState(val);
    useAdminVenueStore.getState().setIsBulkPreviewOpen(val !== null);
  };
  const selectionEnabled = !bulkPreview;
  const {
    handleVenueClick,
    handleVenueListBackgroundClick,
    handleKeyDown,
    commitMarqueeVenueIds,
  } = useAdminVenueListSelection({
    venueList,
    selectVenue,
    selectVenues,
    disabled: !selectionEnabled,
    onSelectionStart: () => setBulkPreview(null),
  });
  const {
    hitVenueIds,
    handleVenueListMouseDown,
    handleVenueListClickCapture,
    marqueeBox,
  } = useAdminVenueListMarquee({
    venueListCompRef,
    commitMarqueeVenueIds,
    enabled: selectionEnabled,
  });
  const multiSelectedVenueIdSet = new Set(multiSelectedVenueIds);
  const marqueeHitVenueIdSet = new Set(hitVenueIds);
  const hasMultiSelected = multiSelectedVenueIds.length > 1;
  const hasViewer = !!activeVenue || hasMultiSelected;

  useEffect(() => {
    listRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!user || !authToken) return;
    void load(1, Number(pageSize), filterData);
  }, [user, authToken, pageSize, filterData, sortData]);

  const handleCreate = () => {
    if (activeVenue && !activeVenue.id) return;
    runAfterUnsavedVenueCheck(() => {
      setBulkPreview(null);
      setBulkResult(null);
      useAdminVenueStore.getState().setHasChanged(false);
      setActiveVenue(emptyVenue());
    });
  };

  const handleBulkClick = () => {
    fileInputRef.current?.click();
  };

  const handleTemplateDownload = async () => {
    try {
      const response = await getVenueTagList();
      await downloadVenueBulkTemplate(response.data.map((tag) => tag.name));
    } catch {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_VALUE_REQUESTED'),
      });
    }
  };

  const handleBulkFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!sizeCheckBig(file, VENUE_BULK_FILE_MAX_SIZE)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('FILE_TOO_BIG_PREVIEW', {
          maxSize: VENUE_BULK_FILE_MAX_SIZE_TEXT,
        }),
      });
      return;
    }

    try {
      const result = await parseVenueBulkSheet(file);
      if (result.totalCount === 0) {
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('MISSING_REQUIRED_FIELD'),
        });
        return;
      }
      if (result.totalCount > VENUE_BULK_MAX_ROWS) {
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', {
            maxCount: VENUE_BULK_MAX_ROWS,
          }),
        });
        return;
      }
      runAfterUnsavedVenueCheck(() => {
        useAdminVenueStore.getState().setHasChanged(false);
        setActiveVenue(null);
        setBulkResult(null);
        setBulkPreview({ fileName: file.name, result });
      });
    } catch (error) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: error instanceof VenueBulkSheetParseError
          ? error.message
          : getAlertMessage('INVALID_VALUE_REQUESTED'),
      });
    }
  };

  const handleBulkConfirm = async () => {
    if (!bulkPreview) return;
    const submitted = await createBulk(
      { venueList: bulkPreview.result.venueList },
      bulkPreview.result.invalidResults,
    );
    if (submitted) setBulkPreview(null);
  };

  return (
    <ListContainer ref={listRef} tabIndex={0} onKeyDown={handleKeyDown}>
      <List
        ref={venueListCompRef}
        onMouseDown={handleVenueListMouseDown}
        onClickCapture={handleVenueListClickCapture}
        onClick={handleVenueListBackgroundClick}
      >
        {venueList.map((venue: SimpleVenueData) => (
          <VenueInfo
            key={venue.id}
            data={venue}
            isSelected={
              !!venue.id
              && (
                String(venue.id) === String(activeVenue?.id ?? '')
                || multiSelectedVenueIdSet.has(String(venue.id))
              )
            }
            isMarqueeHit={marqueeHitVenueIdSet.has(String(venue.id))}
            handleClick={(event) => handleVenueClick(venue, event)}
          />
        ))}
        {marqueeBox && (
          <MarqueeSelectionBox
            style={{
              left: marqueeBox.left,
              top: marqueeBox.top,
              width: marqueeBox.width,
              height: marqueeBox.height,
            }}
          />
        )}
        <BtnContainer data-suppress-marquee>
          <UniversalIconBtn
            size="standard"
            border={true}
            backgroundImg={createIcon}
            onClick={handleCreate}
          />
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleBulkFileChange}
          />
          <UniversalIconBtn
            size="standard"
            border={true}
            backgroundImg={listIcon}
            onClick={handleBulkClick}
          />
          <UniversalIconBtn
            size="standard"
            border={true}
            backgroundImg={downloadIcon}
            onClick={() => { void handleTemplateDownload(); }}
          />
        </BtnContainer>
        {venueList.length === 0 && <EmptyList message="venues" />}
        <ListFill />
      </List>
      {bulkPreview ? (
        <VenueViewerShell>
          <VenueBulkPreview
            fileName={bulkPreview.fileName}
            result={bulkPreview.result}
            onCancel={() => setBulkPreview(null)}
            onConfirm={() => { void handleBulkConfirm(); }}
          />
        </VenueViewerShell>
      ) : bulkResult && !hasViewer ? (
        <VenueViewerShell>
          <VenueBulkResult />
        </VenueViewerShell>
      ) : hasViewer ? (
        <VenueViewerShell>
          {hasMultiSelected ? <MultiVenueViewer /> : <VenueViewer />}
        </VenueViewerShell>
      ) : (
        null
      )}
    </ListContainer>
  );
};

const ListContainer = styled.article`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  outline: none;
`;

const List = styled.section`
  flex: 1;
  width: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 10vh;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  position: relative;
  overflow-y: auto;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 3;
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: 8vh;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5vw;
  z-index: 4;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ListFill = styled.div`
  flex: 1 1 auto;
  align-self: stretch;
  width: 100%;
  min-height: 0;
`;

export default VenueList;
